import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { UiService } from '@services/ui.service';
import { ReportService } from '@services/report.service';
import { SimulationService } from '@services/simulation.service';
import { ChartType } from '@enums/chart-type.enum';
import { ReportDataType } from '@enums/report-data-type.enum';
import { CustomBaseComponent } from '../custom-base/custom-base.component';
import { Simulation } from '@classes/simulation/simulation';
import { Partition } from '@classes/simulation/partition';
import { ReportDataset } from '@classes/report/report-dataset';
import { ReportType } from '@classes/report/report-type';
import { ReportTypeCode } from '@enums/report-type-code.enum';
import { ReportItem } from '@classes/report/report-item';
import { ApiText } from '@classes/api-text';

@Component({
  selector: 'app-custom-simu',
  templateUrl: './custom-simu.component.html',
  styleUrls: ['./custom-simu.component.scss']
})
export class CustomSimuComponent extends CustomBaseComponent implements OnInit, OnDestroy {
    private selectedSimusSub: Subscription;
    chartType = ChartType;

    constructor(
        protected uiSrv: UiService,
        protected reportSrv: ReportService,
        private simulationSrv: SimulationService
    ) {
        super(uiSrv, reportSrv);
        this.dataType = ReportDataType.amount;
     }

    ngOnInit() {
        this.initSubscriptions();
    }

    private initSubscriptions(): void {
        this.selectedSimusSub = this.simulationSrv.selectedSimulationsSubject
        .subscribe(simulations => {
            this.simulations = simulations;
            if(simulations) {
                this.initData();
            }
        });
    }

    private initData(): void {
        this.partitions = this.simulations[0].partitions;
        this.referenceYear = this.simulations[0].lastReferenceYear;

        this.years = this.GetYears(this.simulations[0]);
        if (this.simulations[1]) {
            this.years = this.GetYears(this.simulations[1]).filter(y => this.years.includes(y));
        }
        if (this.simulations[2]) {
            this.years = this.GetYears(this.simulations[2]).filter(y => this.years.includes(y));
        }

        this.rootPartition = this.partitions.find(p => p.partitionDetails.articleType === this.customReport.articleType);
    }

    ngOnDestroy(): void {
        if(this.selectedSimusSub) {
            this.selectedSimusSub.unsubscribe();
        }
    }

    protected generateReportItems(): void {
        super.generateReportItems();

        const reportType: ReportType = {
            code: ReportTypeCode.custom,
            text: new ApiText(this.uiSrv.translate('reports.categories.custom.shortLabel'), ' ', '', '')
        };

        const variation = this.customReport.reportItems[1].variations[0];
        const tableCount = variation.stacks[0].datasets.length;

        for (var tableIndex = 0; tableIndex < tableCount; ++tableIndex)
        {
            const label = variation.stacks[0].datasets[tableIndex].label;

            const reportItem: ReportItem = {
                "title": new ApiText(label, label, `${reportType.text.shortLabel} ${this.customReport.id + 1}`),
                "group": 1000 * (this.customReport.id + 1) + 1 + tableIndex,
                "reportType": reportType,
                "selectedVariationCode": "datatable",
                "variations": [{
                    "code": "datatable",
                    displayType: ChartType.datatable,
                    dataType: ReportDataType.amount,
                    "labels": variation.stacks.map(stack => stack.name),
                    "axesLabels": {
                        "categories": this.uiSrv.translate('reports.barChart.years.shortLabel'),
                        "data": this.uiSrv.translate('reports.barChart.amounts.shortLabel')
                    },
                    "stacks": [{
                            "name": variation.labels[tableIndex],
                            "datasets": variation.labels.map((year, yearIndex) => { return {
                                "label": year,
                                "data": variation.stacks.map((stack, stackIndex) => stack.datasets[tableIndex].data[yearIndex])
                            }})
                        }
                    ]
                }]
            };

            this.customReport.reportItems.push(reportItem);
        }
    }

    protected generateDatasets(partitions: Partition[], selectedCodes: string[]): ReportDataset[] {
        const firstYear = parseInt(this.years[0]);

        return partitions.reduce((accumulator, partition) => {
            const datasets: ReportDataset[] = [];
            const pCode = partition.partitionDetails.code;

            if(selectedCodes.indexOf(pCode) !== -1) {
                const label = partition.partitionDetails.text.shortLabel;
                let data: number[];

                switch(this.dataType) {
                    case ReportDataType.amount:
                        data = partition.simulationValues.years
                            .filter(y => y.year >= firstYear)
                            .map(y => y.amount);
                        break;
                    case ReportDataType.percent:
                    default:
                        data = partition.simulationValues.years
                            .map(y => y.ratio);
                }

                datasets.push({
                    label: label,
                    data: data
                });
            }

            if(partition.partitions) {
                datasets.push(...this.generateDatasets(partition.partitions, selectedCodes));
            }

            return [ ...accumulator, ...datasets];
        }, []);
    }

    private GetYears (simulation: Simulation): string[] {
        const executedYear = parseInt(simulation.lastExecutedYear);
        return simulation
            .partitions[0].simulationValues.years
            .filter(y => y.year > executedYear)
            .map(y => y.year.toString());
    }
}
